/**
 * Parser and validator for the interactive onboarding flow DSL — the fenced
 * ```onboarding JSON block the agent emits in chat. See
 * services/agent-core/docs/specs/interactive-onboarding.md.
 *
 * Anything that does not validate strictly renders as a plain code block
 * (OnboardingFlowCard's fallback): this is a targeting language, not a
 * styling channel.
 */

import type {
  OnboardingFlow,
  OnboardingFlowStep,
  OnboardingStepPlacement,
} from '../config';

// The flow DSL types are declared in config/types.ts and re-exported here:
// this module owns their parsing, the config owns the contract itself.
export type { OnboardingFlow, OnboardingFlowStep, OnboardingStepPlacement };

export const ONBOARDING_LANGUAGE = 'onboarding';

// Targeting policy: a flow selector must anchor on a data-* attribute — any
// one, any value. The data-* attributes are the only thing the agent's DOM
// view (page_dom) guarantees about a page, so they are the only accepted
// prior; anything the agent saw in the tree must validate. Everything beyond
// the anchor is full CSS, validated by the platform's own selector parser
// (via a detached probe document) instead of a hand-maintained grammar:
// selectors are inert — they only ever reach querySelector, never markup —
// so the browser accepting `:not`, `:has`, combinators, etc. is safe and
// removes the whack-a-mole of whitelisting pseudo-classes and value shapes.
export const ONBOARDING_SELECTOR_PATTERN = /\[data-[^\]]*\]/;

const MAX_SELECTOR_LENGTH = 200;

let selectorProbe: Document | null = null;

export const isValidOnboardingSelector = (selector: string): boolean => {
  if (!selector || selector.length > MAX_SELECTOR_LENGTH) {
    return false;
  }
  if (!ONBOARDING_SELECTOR_PATTERN.test(selector)) {
    return false;
  }
  try {
    selectorProbe ??= document.implementation.createHTMLDocument(
      'onboarding-selector-probe',
    );
    selectorProbe.querySelector(selector);
    return true;
  } catch {
    return false;
  }
};

/** querySelector that treats an invalid selector as "no match" — a bad step
 * must skip itself, never kill the tour's match pass. */
export const safeQuerySelector = (selector: string): Element | null => {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
};

const MAX_TITLE_LENGTH = 120;
const MAX_PAGE_PATH_LENGTH = 200;
// URL path only: rooted, no query, no fragment, no whitespace.
const PAGE_PATH_PATTERN = /^\/[^\s?#]*$/;
const MAX_STEP_TEXT_LENGTH = 400;
const MAX_STEPS = 8;

const PLACEMENTS: readonly OnboardingStepPlacement[] = [
  'top',
  'bottom',
  'left',
  'right',
];

const boundedString = (value: unknown, max: number): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > max) {
    return undefined;
  }
  return trimmed;
};

const parseStep = (raw: unknown): OnboardingFlowStep | null => {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const selector = boundedString(record.selector, MAX_SELECTOR_LENGTH);
  if (!selector || !isValidOnboardingSelector(selector)) {
    return null;
  }
  const text = boundedString(record.text, MAX_STEP_TEXT_LENGTH);
  if (!text) {
    return null;
  }
  const placement = PLACEMENTS.includes(
    record.placement as OnboardingStepPlacement,
  )
    ? (record.placement as OnboardingStepPlacement)
    : 'bottom';
  const step: OnboardingFlowStep = { selector, text, placement };
  const title = boundedString(record.title, MAX_TITLE_LENGTH);
  if (title) {
    step.title = title;
  }
  return step;
};

/**
 * Parses the body of an ```onboarding fenced block. Returns null for
 * anything invalid or empty — the caller falls back to plain code rendering.
 */
export const parseOnboardingFlow = (code: string): OnboardingFlow | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(code);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const rawSteps = Array.isArray(record.steps) ? record.steps : [];
  if (rawSteps.length === 0 || rawSteps.length > MAX_STEPS) {
    return null;
  }
  const steps: OnboardingFlowStep[] = [];
  for (const raw of rawSteps) {
    const step = parseStep(raw);
    if (!step) {
      return null;
    }
    steps.push(step);
  }
  const flow: OnboardingFlow = { steps };
  const title = boundedString(record.title, MAX_TITLE_LENGTH);
  if (title) {
    flow.title = title;
  }
  const pagePath = boundedString(record.pagePath, MAX_PAGE_PATH_LENGTH);
  if (pagePath && PAGE_PATH_PATTERN.test(pagePath)) {
    flow.pagePath = pagePath;
  }
  return flow;
};

/**
 * Extracts an onboarding flow from a completed assistant message body.
 * Returns the first valid ```onboarding block, or null.
 */
export const extractOnboardingFlow = (
  content: string,
): OnboardingFlow | null => {
  const pattern = /```onboarding\r?\n([\s\S]*?)(?:```|$)/g;
  for (const match of content.matchAll(pattern)) {
    const flow = parseOnboardingFlow(match[1]);
    if (flow) {
      return flow;
    }
  }
  return null;
};
